'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Typography } from '@/components/ui/typography';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Card } from '@/components/ui/card';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { SectionHeading } from '@/components/frontend/home/section-heading';

export function MobileAppSupport() {
    const t = useTranslations('home.mobileAppSupport');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    // Fallback values in case translations are missing
    const defaultImageSrc = '/images/home/mobileviewHome.webp';
    const defaultImageSrcDark = '/images/home/best-mlm-software-usa.webp';
    const defaultImageAlt = 'Business MLM Mobile App Dashboard';

    const content = {
        pillLabel: t('pillLabel'),
        title: t('title'),
        subtitle: t('subtitle'),
        paragraph1: t('paragraph1'),
        paragraph2: t('paragraph2'),
        imageSrc: (() => {
            const src = t('imageSrc');
            // Check if translation returned a valid path (starts with / or http) or is the key itself
            return src.startsWith('/') || src.startsWith('http') ? src : defaultImageSrc;
        })(),
        imageSrcDark: (() => {
            const src = t('imageSrcDark');
            return src.startsWith('/') || src.startsWith('http') ? src : defaultImageSrcDark;
        })(),
        imageAlt: (() => {
            const alt = t('imageAlt');
            // Check if translation returned the key (contains dots) or is valid text
            return alt.includes('.') && alt.includes('imageAlt') ? defaultImageAlt : alt;
        })(),
    };

    return (
        <SectionWrapper ref={sectionRef.ref} variant="default" padding="md" className="relative">
            <div className="mx-auto">
                <Card
                    title=""
                    description=""
                    className="!bg-gradient-to-b !from-gray-50 !via-white !to-gray-50 dark:!bg-gradient-to-b dark:!from-gray-950 dark:!via-gray-900 dark:!to-black !border-gray-200 dark:!border-gray-800 p-0 overflow-hidden"
                    animationDelay={200}
                >
                    <div className="grid lg:grid-cols-[2fr_1fr] gap-0">
                        {/* Left Section - Text (2/3 width) */}
                        <div className="px-6 lg:px-6 flex items-center">
                            <div
                                className={`max-w-2xl transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                                    }`}
                            >
                                <SectionHeading
                                    pill={content.pillLabel}
                                    title={content.title}
                                    subtitle=""
                                    isVisible={sectionRef.isVisible}
                                    align="left"
                                    maxWidthClass="max-w-3xl"
                                    className="!mb-6 [&_h2]:text-gray-900 dark:[&_h2]:text-white"
                                />

                                <div className="space-y-6 text-base md:text-lg lg:text-xl leading-relaxed">
                                    <Typography variant="p" className="text-gray-700 dark:text-gray-300">
                                        {content.paragraph1}
                                    </Typography>

                                    <Typography variant="p" className="text-gray-700 dark:text-gray-300">
                                        {content.paragraph2}
                                    </Typography>
                                </div>
                            </div>
                        </div>

                        {/* Right Section - Mobile App Image (1/3 width) */}
                        <div className="px-6 md:px-10 lg:px-12 py-6 flex items-center justify-center h-full">
                            <div
                                className={`relative w-full h-full max-w-sm transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                                    }`}
                                style={{ transitionDelay: '400ms' }}
                            >
                                <Image
                                    src={content.imageSrc}
                                    alt="MLM Mobile App - Business MLM Software Mobile Dashboard for Network Marketing on iOS and Android"
                                    width={400}
                                    height={800}
                                    className="w-full h-full object-contain block dark:hidden"
                                    priority
                                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                                />
                                <Image
                                    src={content.imageSrcDark}
                                    alt="MLM Mobile App - Business MLM Software Mobile Dashboard for Network Marketing on iOS and Android"
                                    width={400}
                                    height={800}
                                    className="w-full h-full object-contain hidden dark:block"
                                    priority
                                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                                />
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        </SectionWrapper>
    );
}

